import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { StatCard } from '@/components/StatCard'
import { DashboardChart } from '@/components/DashboardChart'
import { useDistributorWorkspace } from '@/contexts/DistributorWorkspaceContext'
import {
  activeDistributorPrograms,
  describeMyDistributorEmptyState,
  fetchMyDistributorPaymentsAll,
  formatMyDistributorCurrency,
  manualDistributorPrograms,
} from '@/lib/my-distributor'
import { MyDistributorEmptyState } from '@/pages/SouDistribuidorPage/MyDistributorEmptyState'
import type { MyDistributorPaymentItem } from '@/types/my-distributor'
import styles from './SouDistribuidorPage.module.css'

const MONTH_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function buildMonthlyChart(payments: MyDistributorPaymentItem[]) {
  const map = new Map<string, { vendas: number; comissoes: number }>()
  for (const payment of payments) {
    const date = new Date(payment.paid_at)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const entry = map.get(key) ?? { vendas: 0, comissoes: 0 }
    entry.vendas += payment.payment_amount
    entry.comissoes += payment.distributor_commission_amount
    map.set(key, entry)
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-12)
    .map(([key, values]) => {
      const month = parseInt(key.split('-')[1], 10) - 1
      return {
        mes: `${MONTH_LABELS[month]}/${key.split('-')[0].slice(2)}`,
        ...values,
      }
    })
}

export function SouDistribuidorDashboardPage() {
  const { programs, global, profile, loginContext, isLoading, error, reload } =
    useDistributorWorkspace()
  const [payments, setPayments] = useState<MyDistributorPaymentItem[]>([])
  const [isPaymentsLoading, setIsPaymentsLoading] = useState(true)
  const [paymentsError, setPaymentsError] = useState<string | null>(null)

  const loadPayments = useCallback(async () => {
    setIsPaymentsLoading(true)
    setPaymentsError(null)
    try {
      const res = await fetchMyDistributorPaymentsAll({ per_page: 100 })
      setPayments(res.items)
    } catch (err) {
      setPaymentsError(
        err instanceof Error ? err.message : 'Erro ao carregar vendas do distribuidor.'
      )
    } finally {
      setIsPaymentsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isLoading && programs.length > 0) {
      void loadPayments()
    } else if (!isLoading) {
      setIsPaymentsLoading(false)
    }
  }, [isLoading, programs.length, loadPayments])

  const emptyMessage = useMemo(
    () => describeMyDistributorEmptyState(loginContext, profile, programs),
    [loginContext, profile, programs]
  )

  const active = useMemo(() => activeDistributorPrograms(programs), [programs])
  const manual = useMemo(() => manualDistributorPrograms(programs), [programs])
  const totalBalance = useMemo(() => manual.reduce((sum, p) => sum + p.balance, 0), [manual])
  const totalIndications = useMemo(
    () => programs.reduce((acc, p) => acc + p.metrics.payments_count, 0),
    [programs]
  )
  const ticketMedio = useMemo(
    () => (totalIndications > 0 ? global.total_sales / totalIndications : 0),
    [global.total_sales, totalIndications]
  )
  const monthlyData = useMemo(() => buildMonthlyChart(payments), [payments])
  const topPrograms = useMemo(
    () => [...programs].sort((a, b) => b.metrics.total_sales - a.metrics.total_sales).slice(0, 5),
    [programs]
  )

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
        <span>Carregando...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
        <span>{error}</span>
        <button type="button" className={styles.retryBtn} onClick={() => void reload()}>
          Tentar novamente
        </button>
      </div>
    )
  }

  if (programs.length === 0) {
    return <MyDistributorEmptyState icon="mdi:truck-delivery-outline" message={emptyMessage} />
  }

  const isChartLoading = isPaymentsLoading

  return (
    <div className={styles.page}>
      <div className={styles.statsGrid}>
        <StatCard
          title="Programas Ativos"
          value={active.length}
          icon="🏢"
          subtitle={`${programs.length} no total`}
        />
        <StatCard
          title="Vendas Atribuídas"
          value={totalIndications.toLocaleString('pt-BR')}
          icon="🤝"
          subtitle="Pagamentos na sua rede"
        />
        <StatCard
          title="Vendas Geradas"
          value={formatMyDistributorCurrency(global.total_sales)}
          icon="💰"
        />
        <StatCard
          title="Suas Comissões"
          value={formatMyDistributorCurrency(global.total_commission)}
          icon="💸"
        />
      </div>

      <div className={styles.statsGridSecondary}>
        <StatCard
          title="Ticket Médio"
          value={formatMyDistributorCurrency(ticketMedio)}
          icon="🎫"
        />
        {manual.length > 0 ? (
          <StatCard
            title="Saldo Manual"
            value={formatMyDistributorCurrency(totalBalance)}
            icon="👛"
            subtitle={`${manual.length} programa(s) · ver aba Saldo`}
          />
        ) : (
          <StatCard
            title="Pagamento"
            value="Automático (Doey)"
            icon="⚡"
            subtitle="Comissão via split no PIX"
          />
        )}
      </div>

      <div className={styles.mainContent}>
        <div className={styles.chartSection}>
          <DashboardChart title="Vendas e comissões do distribuidor">
            {paymentsError ? (
              <div className={styles.emptyChart}>
                <span>{paymentsError}</span>
                <button type="button" className={styles.retryBtn} onClick={() => void loadPayments()}>
                  Tentar novamente
                </button>
              </div>
            ) : isChartLoading ? (
              <div className={styles.emptyChart}>
                <Icon icon="mdi:loading" className={styles.spinner} />
              </div>
            ) : monthlyData.length === 0 ? (
              <div className={styles.emptyChart}>
                <Icon icon="mdi:chart-line" />
                <span>Sem vendas pagas para exibir no período</span>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(59,22,54,0.1)" />
                  <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6c757d' }} />
                  <YAxis
                    tick={{ fontSize: 12, fill: '#6c757d' }}
                    tickFormatter={(v) => `R$${(Number(v) / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(value) =>
                      formatMyDistributorCurrency(typeof value === 'number' ? value : 0)
                    }
                    contentStyle={{
                      background: '#ffffff',
                      border: '1px solid rgba(59,22,54,0.2)',
                      borderRadius: 8,
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="vendas"
                    name="Vendas"
                    stroke="#5e17eb"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="comissoes"
                    name="Comissões"
                    stroke="#d4006b"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </DashboardChart>
        </div>

        <div className={styles.programList}>
          <h3 className={styles.programListTitle}>
            <Icon icon="mdi:trophy" />
            Seus programas
          </h3>
          {topPrograms.length === 0 ? (
            <div className={styles.emptyBlock}>
              <span>Nenhum programa com vendas ainda</span>
            </div>
          ) : (
            <ul className={styles.programItems}>
              {topPrograms.map((program) => (
                <li key={program.distributor_id} className={styles.programItem}>
                  <span className={styles.programName}>{program.institution.name}</span>
                  <span className={styles.programMeta}>
                    {program.metrics.payments_count} vendas ·{' '}
                    {formatMyDistributorCurrency(program.metrics.total_sales)}
                  </span>
                  <span
                    className={program.is_active ? styles.badgeActive : styles.badgeInactive}
                  >
                    {program.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <p style={{ marginTop: 'var(--spacing-md)', fontSize: 'var(--font-size-xs)' }}>
            <Link to="/sou-distribuidor/afiliados/lista">Gerenciar afiliados</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
